import { useState, useEffect } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { DataGrid, itIT, GridToolbar, useGridApiRef } from '@mui/x-data-grid';		//https://mui.com/x/react-data-grid/ [MIT version]
import { Box, Alert, Button, TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { Link } from 'react-router-dom';

import EngineeringIcon from '@mui/icons-material/Engineering';
import DoneOutlineIcon from '@mui/icons-material/DoneOutline';
import ErrorIcon from '@mui/icons-material/Error';

import Api from '../../Services/Api.js';
import { PAGE_SIZE, pageSizeOptions, timeTableIntervals as optionsTimeTableIntervals, OPERATOR_ROLE } from '../../Config/constants.js';
import moment from 'moment';
import { useAuth } from '../../Auth/ProvideAuth.js';
import ActionButton from '../ActionButton.js';

function ReportAppointmentsOverview() {

	const auth = useAuth();
	const apiRef = useGridApiRef();	//https://mui.com/x/api/data-grid/
	const DEFAULT_VALUE = "0";

	const [rows, setRows] = useState([]);
	const [displayAlert, setDisplayAlert] = useState(0);
	const [openDialog, setOpenDialog] = useState(false);
	const [selectedId, setSelectedId] = useState(null);
	const [days, setDays] = useState(DEFAULT_VALUE);

	const theme = createTheme({
			palette: {
				primary: { main: '#11cb5f' },
				warning: { main: '#ffc107' },
			},
		},
		itIT,
	);

	const columns = [
		{
			field: 'appointment_at',
			headerName: 'Data',
			flex: 1,
			type: 'dateTime',
			valueGetter: (params) => params.row.appointment_at ? new Date(params.row.appointment_at) : null,
			valueFormatter: (params) => params.value ? params.value.toLocaleDateString('it-IT') + ' ' + params.value.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) : '',
		},
		{
			field: 'appointment_type',
			headerName: 'Tipo',
			flex: 1,
		},
		{
			field: 'customer',
			headerName: 'Cliente',
			flex: 2,
			valueGetter: (params) => {
				return params.row.customer_surname + " " + params.row.customer_name + " [Cod.impianto:" + params.row.unique_code + "]"
			},
		},
		{
			field: 'address',
			headerName: 'Città',
			flex: 2,
			valueGetter: (params) => {
				return (
					(params.row.installation_city ? params.row.installation_city : params.row.customer_city) +
					" [" +
					(params.row.installation_area ? params.row.installation_area : params.row.customer_area) + "]"
				);
			},
		},
		{
			field: 'technician',
			headerName: 'Tecnico',
			flex: 2,
			valueGetter: (params) => {
				return params.row.technician_name + " " + params.row.technician_surname
			},
		},
		{
			field: 'id',
			headerName: 'Azioni',
			flex: 1,
			renderCell: (params) => {
				console.log(params);
				function onEdit(e) {
					e.stopPropagation(); // don't select this row after clicking
					setSelectedId(params.value);
					setOpenDialog(true);
				};

				const isClosed = params.row.closed_appointment === 1;

				return <>
					{auth.lonyUser.roles[0].name !== OPERATOR_ROLE && (
						<ActionButton title={"Lavora Appuntamento"} onClick={onEdit}>
							<EngineeringIcon />
						</ActionButton>
					)}
					<ActionButton title={`Stato: ${isClosed ? 'Chiuso' : 'Aperto'}`}>
						{isClosed ? <>
							<DoneOutlineIcon />
						</> : <>
							<ErrorIcon sx={{color: theme.palette.warning.main}}/>
						</>}
					</ActionButton>
				</>
			}
		},
	]

	function handleClose() {
		setOpenDialog(false);
		setSelectedId(null);
	};
	function handleOk() {
		window.open(`/cms/appointments/work/${selectedId}`, '_blank');
		// Reset after open new tab
		setOpenDialog(false);
		setSelectedId(null);
	};

	function arrayIntervalsResponse() {
		return (
			[DEFAULT_VALUE, ...optionsTimeTableIntervals].map((interval) => (
				<MenuItem key={interval} value={interval}>
					{interval === '0' ? 'Nessun filtro' : `${interval} giorni`}
				</MenuItem>
			))
		);
	}

	function extraFilter(selectedValue) {
		setDays(selectedValue);
	}

	useEffect(() => {
		try {
			Api.get({endpoint: `report/appointments-overview/${days}`, contentType : 'application/json'})
			.then(response => {
				setRows(response.data.list);
			})
			.catch(error => {
				console.log(JSON.stringify(error));
				setDisplayAlert(-1);
			});
		} catch (error) {
			console.log(JSON.stringify(error));
			setDisplayAlert(-1);
		}
	}, [days]);	// eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<h5>Report Appuntamenti NON CHIUSI</h5>

			<div className="row styled">
				<div className="col-md-2">
					<TextField
						fullWidth
						label="Intervallo di visualizzazione"
						name="interval"
						className="styled"
						select
						defaultValue = {DEFAULT_VALUE}
						InputProps={{
							style: {color: theme.palette.primary.main}
						}}
						onChange={(e) => {
							extraFilter(e.target.value);
						}}
					>
						{arrayIntervalsResponse()}
					</TextField>
				</div>
				<div className="col-md-10">
					<p style={{fontSize: '0.8em'}}>
						{days && <>
						{days === '0' ? <span>Nessun filtro</span> : <span>Dal: {moment().subtract(days, 'days').format('DD/MM/YYYY')} - Al: {moment().format('DD/MM/YYYY')}</span>}
						</>}
					</p>
				</div>
			</div>

			<Box sx={{ width: '100%', minWidth: '800px' }}>
				<ThemeProvider theme={theme}>
					<DataGrid
						sx={{ minHeight: '300px' }}
						rows={rows}
						columns={columns}
						initialState={{
							pagination: {
								paginationModel: {
									pageSize: PAGE_SIZE,
								},
							},
						}}
						apiRef={apiRef}
						slots={{
							toolbar: GridToolbar,
						}}
						slotProps={{
							columnsPanel: {
								disableHideAllButton: true,
							},
							toolbar: {
								showQuickFilter: true,
							},
						}}
						pageSizeOptions={pageSizeOptions}
					/>
					<div className="mt-2">
						<Link to={'/cms/reports'} >
							<Button variant="contained">Torna alla lista</Button>
						</Link>
					</div>
				</ThemeProvider>
				{displayAlert === -1 && (
					<Alert severity="error">Si è verificato un errore imprevisto. Riprovare più tardi</Alert>
				)}
			</Box>

			<Dialog
				open={openDialog}
				onClose={handleClose}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
			>
				<DialogTitle id="alert-dialog-title">
					{"Procedere con la lavorazione dell'appuntamento?"}
				</DialogTitle>
				<DialogContent>
					<DialogContentText id="alert-dialog-description">
						Verrà aperta una nuova scheda per procedere alla lavorazione dell'appuntamento.<br/>
						Vuoi comunque procedere a lavorare l'appuntamento?
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose} autoFocus>Annulla</Button>
					<Button onClick={handleOk}>Procedi</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}

export default ReportAppointmentsOverview;
